import React, { useEffect, useState } from "react";
import PageTitle from "../../PageTitle";
import LeaveNavigation from "./navigation/LeaveNavigation";
import useAxios from "../../../utilities/useAxios";
import { getCurrentUserProfile } from "../../../utilities/AuthService";
import {
	Form,
	Input,
	DatePicker,
	Select,
	Button,
	message,
	Card,
	ConfigProvider,
	Space,
	Modal,
	Table,
	Tag,
	Layout,
	Radio,
	TimePicker,
	Switch,
	Alert,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import useIsMobile from "../../../hooks/useIsMobile";
import PageHeader from "../../common/PageHeader";

dayjs.locale("ko");

const { Option } = Select;
const { TextArea } = Input;
const { Content } = Layout;

const LOCAL_TRIP_LATITUDE = 37.490239;
const LOCAL_TRIP_LONGITUDE = 126.846003;
const LOCAL_TRIP_RADIUS_METERS = 350;

const getJobLabel = (job, gender) => {
	if (job === "목사") return "목사";
	if (job === "전도사") return gender === "남" ? "남전도사" : "여전도사";
	if (job === "교직원") return gender === "남" ? "남직원" : "여직원";
	return job || "-";
};

const toRadians = (degrees) => (degrees * Math.PI) / 180;

const distanceMeters = (lat1, lng1, lat2, lng2) => {
	const earthRadius = 6371000;
	const dLat = toRadians(lat2 - lat1);
	const dLng = toRadians(lng2 - lng1);
	const a =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(toRadians(lat1)) *
			Math.cos(toRadians(lat2)) *
			Math.sin(dLng / 2) ** 2;
	return 2 * earthRadius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const formatDistance = (meters) => {
	if (meters < 1000) return `약 ${Math.round(meters)}m`;
	return `약 ${(meters / 1000).toFixed(1)}km`;
};

const getCurrentCoordinates = () =>
	new Promise((resolve, reject) => {
		if (!navigator.geolocation) {
			reject(new Error("unsupported"));
			return;
		}
		navigator.geolocation.getCurrentPosition(
			(position) =>
				resolve({
					latitude: position.coords.latitude,
					longitude: position.coords.longitude,
					accuracy: position.coords.accuracy,
				}),
			reject,
			{
				enableHighAccuracy: true,
				timeout: 15000,
				maximumAge: 0,
			},
		);
	});

const getLocalTripLocationError = async () => {
	let coords;
	try {
		coords = await getCurrentCoordinates();
	} catch (error) {
		if (error?.message === "unsupported") {
			return "이 브라우저에서는 위치를 확인할 수 없어 시내출장을 신청할 수 없습니다.";
		}
		if (error?.code === 1) {
			return "시내출장 신청을 위해 위치 권한이 필요합니다. 브라우저에서 위치 접근을 허용해주세요.";
		}
		return "현재 위치를 확인하지 못했습니다. 위치 서비스를 켠 뒤 다시 시도해주세요.";
	}

	const distance = distanceMeters(
		coords.latitude,
		coords.longitude,
		LOCAL_TRIP_LATITUDE,
		LOCAL_TRIP_LONGITUDE,
	);
	if (distance > LOCAL_TRIP_RADIUS_METERS) {
		const accuracyText =
			coords.accuracy == null
				? "알 수 없음"
				: formatDistance(coords.accuracy);
		return `시내출장은 교회 내에서만 신청 가능합니다. 위도 ${coords.latitude.toFixed(6)}, 경도 ${coords.longitude.toFixed(6)}, 정확도 ${accuracyText}, 거리 ${formatDistance(distance)}`;
	}
	return null;
};

const ApplyForBusinessTrip = () => {
	const isMobile = useIsMobile();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [userProfile, setUserProfile] = useState(null);
	const [tripKind, setTripKind] = useState("출장");
	const [returnType, setReturnType] = useState(null);
	const [additionalUsersModalVisible, setAdditionalUsersModalVisible] =
		useState(false);
	const [availableUsers, setAvailableUsers] = useState([]);
	const [selectedAdditionalUsers, setSelectedAdditionalUsers] = useState(
		[],
	);
	const [searchText, setSearchText] = useState("");
	const [filteredUsers, setFilteredUsers] = useState([]);

	useEffect(() => {
		message.config({ top: 150 });
		return () => message.config({ top: 8 });
	}, []);

	useEffect(() => {
		(async () => {
			try {
				const profile = await getCurrentUserProfile();
				setUserProfile(profile);
				form.setFieldsValue({
					name: profile.name,
					position:
						profile.classification_name ===
						"교역자"
							? "교역자"
							: "교직원",
					application_date: dayjs(),
				});
			} catch (e) {
				message.error(
					"사용자 정보를 불러오는데 실패했습니다.",
				);
			}
		})();
	}, [form]);

	const fetchAvailableUsers = async () => {
		try {
			const response = await useAxios.get(
				"/users/clergy-staff/",
			);
			if (response.status === 200) {
				setAvailableUsers(response.data);
				setFilteredUsers(response.data);
			}
		} catch (e) {
			message.error(
				"교역자·교직원 목록을 불러오지 못했습니다.",
			);
		}
	};

	useEffect(() => {
		const q = searchText.trim().toLowerCase();
		if (!q) {
			setFilteredUsers(availableUsers);
			return;
		}
		setFilteredUsers(
			availableUsers.filter((u) =>
				String(u.name || "")
					.toLowerCase()
					.includes(q),
			),
		);
	}, [searchText, availableUsers]);

	const onFinish = async (values) => {
		setLoading(true);
		try {
			if (tripKind === "시내출장") {
				const locationError =
					await getLocalTripLocationError();
				if (locationError) {
					message.error(locationError);
					return;
				}
			}
			let formData;
			if (tripKind === "출장") {
				const start = values.start_date;
				const end = values.end_date;
				if (!start || !end) {
					message.error(
						"시작일과 종료일을 선택해주세요.",
					);
					return;
				}
				if (end.isBefore(start, "day")) {
					message.error(
						"종료일은 시작일 이후여야 합니다.",
					);
					return;
				}
				formData = {
					leave_type: "출장",
					trip_title: values.trip_title,
					reason: values.reason,
					trip_background: values.trip_background,
					start_date: start.format("YYYY-MM-DD"),
					end_date: end.format("YYYY-MM-DD"),
					leave_date: start.format("YYYY-MM-DD"),
					trip_location: values.trip_location,
					additional_users:
						values.additional_users || [],
					trip_includes_lodging: Boolean(
						values.trip_includes_lodging,
					),
					trip_transport: values.trip_transport,
					trip_note: values.trip_note || "",
				};
			} else {
				formData = {
					leave_type: "시내출장",
					leave_date: values.leave_date.format(
						"YYYY-MM-DD",
					),
					reason: values.reason,
					trip_location: values.trip_location,
					additional_users:
						values.additional_users || [],
					local_trip_departure_time:
						values.local_trip_departure_time.format(
							"HH:mm:ss",
						),
					local_trip_return_type:
						values.local_trip_return_type,
				};
				if (
					values.local_trip_return_type ===
					"직접 선택"
				) {
					formData.local_trip_return_time =
						values.local_trip_return_time.format(
							"HH:mm:ss",
						);
				}
			}
			const response = await useAxios.post(
				"/utils/leave/apply/",
				formData,
			);
			if (response.status === 201) {
				message.success(
					tripKind === "시내출장"
						? "시내출장 보고가 접수되었습니다."
						: "출장 신청이 완료되었습니다.",
				);
				form.resetFields();
				setSelectedAdditionalUsers([]);
				setReturnType(null);
				if (userProfile) {
					form.setFieldsValue({
						name: userProfile.name,
						position:
							userProfile.classification_name ===
							"교역자"
								? "교역자"
								: "교직원",
						application_date: dayjs(),
					});
				}
			}
		} catch (error) {
			const data = error.response?.data;
			const msg =
				data?.error ||
				(data && Object.values(data).flat()?.[0]) ||
				"신청 처리 중 오류가 발생했습니다.";
			message.error(String(msg));
		} finally {
			setLoading(false);
		}
	};

	const disabledDate = (current) => current && current.day() === 0;

	const rowSelection = {
		selectedRowKeys: selectedAdditionalUsers.map((u) => u.id),
		preserveSelectedRowKeys: true,
		onChange: (selectedRowKeys) => {
			setSelectedAdditionalUsers((prev) => {
				const byId = new Map(
					availableUsers.map((u) => [u.id, u]),
				);
				return selectedRowKeys
					.map(
						(id) =>
							byId.get(id) ??
							prev.find(
								(u) =>
									u.id ===
									id,
							),
					)
					.filter(Boolean);
			});
		},
	};

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="출장 신청" />
			<Layout className="member-layout">
				<LeaveNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="member-content__inner">
							<div className="member-content__form-container page leave-apply-form">
								<PageHeader title="출장 신청" />
								<Card className="surface-card">
									<Radio.Group
										value={
											tripKind
										}
										onChange={(
											e,
										) => {
											setTripKind(
												e
													.target
													.value,
											);
											setReturnType(
												null,
											);
											form.resetFields(
												[
													"trip_title",
													"reason",
													"trip_background",
													"start_date",
													"end_date",
													"trip_location",
													"leave_date",
													"local_trip_departure_time",
													"local_trip_return_type",
													"local_trip_return_time",
												],
											);
										}}
										style={{
											marginBottom: 16,
										}}
										optionType="button"
										buttonStyle="solid"
										options={[
											{
												label: "출장",
												value: "출장",
											},
											{
												label: "시내출장",
												value: "시내출장",
											},
										]}
									/>
									<Alert
										type="info"
										showIcon
										style={{
											marginBottom: 16,
										}}
										message={
											<>
												<div>
													<strong>
														출장:
													</strong>{" "}
													서울
													권역
													내·외{" "}
													<strong>
														숙박
														공무
													</strong>{" "}
													또는{" "}
													<strong>
														서울
														권역
														외
														당일
														공무
													</strong>
												</div>
												<div>
													<strong>
														시내출장:
														서울
														권역
														내
														당일
														공무
													</strong>
												</div>
											</>
										}
									/>
									{tripKind ===
										"시내출장" && (
										<Alert
											type="info"
											showIcon
											style={{
												marginBottom: 16,
											}}
											message="시내출장은 승인이 필요하지 않은 보고 사항입니다. 교회 내에서만 신청할 수 있으며, 신청 시 위치 권한이 필요합니다."
										/>
									)}
									<Form
										form={
											form
										}
										layout="vertical"
										onFinish={
											onFinish
										}>
										<Form.Item
											name="name"
											label="이름">
											<Input
												disabled
											/>
										</Form.Item>
										<Form.Item
											name="position"
											label="직분">
											<Select
												disabled>
												<Option value="교역자">
													교역자
												</Option>
												<Option value="교직원">
													교직원
												</Option>
											</Select>
										</Form.Item>
										{tripKind ===
										"출장" ? (
											<>
												<Form.Item
													name="trip_title"
													label="제목"
													rules={[
														{
															required: true,
															message: "제목을 입력해주세요",
														},
													]}>
													<Input />
												</Form.Item>
												<Form.Item
													name="reason"
													label="목적(업무 내용)">
													<TextArea
														rows={
															3
														}
													/>
												</Form.Item>
												<Form.Item
													name="trip_background"
													label="배경 및 필요성">
													<TextArea
														rows={
															3
														}
													/>
												</Form.Item>
												<Form.Item
													name="start_date"
													label="시작일"
													rules={[
														{
															required: true,
															message: "시작일을 선택해주세요",
														},
													]}>
													<DatePicker
														inputReadOnly={isMobile}
														style={{
															width: "100%",
														}}
														disabledDate={
															disabledDate
														}
														format="YYYY-MM-DD"
													/>
												</Form.Item>
												<Form.Item
													name="end_date"
													label="종료일"
													rules={[
														{
															required: true,
															message: "종료일을 선택해주세요",
														},
													]}>
													<DatePicker
														inputReadOnly={isMobile}
														style={{
															width: "100%",
														}}
														disabledDate={
															disabledDate
														}
														format="YYYY-MM-DD"
													/>
												</Form.Item>
												<Form.Item
													name="trip_location"
													label="지역/도시명"
													rules={[
														{
															required: true,
															message: "지역/도시명을 입력해주세요",
														},
													]}>
													<Input />
												</Form.Item>
												<Form.Item
													name="additional_users"
													label="동행 인원">
													<Space
														direction="vertical"
														style={{
															width: "100%",
														}}>
														<Button
															type="dashed"
															block
															icon={
																<UserOutlined />
															}
															onClick={() => {
																setAdditionalUsersModalVisible(
																	true,
																);
																fetchAvailableUsers();
															}}>
															동행
															인원
															선택
														</Button>
														{selectedAdditionalUsers.map(
															(
																user,
															) => (
																<Tag
																	key={
																		user.id
																	}
																	closable
																	onClose={() => {
																		const next =
																			selectedAdditionalUsers.filter(
																				(
																					u,
																				) =>
																					u.id !==
																					user.id,
																			);
																		setSelectedAdditionalUsers(
																			next,
																		);
																		form.setFieldsValue(
																			{
																				additional_users:
																					next.map(
																						(
																							u,
																						) =>
																							u.id,
																					),
																			},
																		);
																	}}>
																	{
																		user.name
																	}{" "}
																	(
																	{getJobLabel(
																		user.job,
																		user.gender,
																	)}

																	)
																</Tag>
															),
														)}
													</Space>
												</Form.Item>
												<Form.Item
													name="trip_includes_lodging"
													label="숙박포함 여부"
													valuePropName="checked">
													<Switch
														checkedChildren="포함"
														unCheckedChildren="미포함"
													/>
												</Form.Item>
												<Form.Item
													name="trip_transport"
													label="예정 교통편"
													rules={[
														{
															required: true,
															message: "교통편을 선택해주세요",
														},
													]}>
													<Select>
														{[
															"항공",
															"열차",
															"차량",
															"기타",
														].map(
															(
																t,
															) => (
																<Option
																	key={
																		t
																	}
																	value={
																		t
																	}>
																	{
																		t
																	}
																</Option>
															),
														)}
													</Select>
												</Form.Item>
												<Form.Item
													name="trip_note"
													label="비고">
													<TextArea
														rows={
															2
														}
													/>
												</Form.Item>
											</>
										) : (
											<>
												<Form.Item
													name="leave_date"
													label="출장 일자"
													rules={[
														{
															required: true,
															message: "출장 일자를 선택해주세요",
														},
													]}>
													<DatePicker
														inputReadOnly={isMobile}
														style={{
															width: "100%",
														}}
														disabledDate={
															disabledDate
														}
														format="YYYY-MM-DD"
													/>
												</Form.Item>
												<Form.Item
													name="reason"
													label="출장 목적"
													rules={[
														{
															required: true,
															message: "출장 목적을 입력해주세요",
														},
													]}>
													<TextArea
														rows={
															3
														}
													/>
												</Form.Item>
												<Form.Item
													name="trip_location"
													label="행선지"
													rules={[
														{
															required: true,
															message: "행선지를 입력해주세요",
														},
													]}>
													<Input placeholder="행선지를 입력하세요" />
												</Form.Item>
												<Form.Item
													name="local_trip_departure_time"
													label="출발 시각"
													rules={[
														{
															required: true,
															message: "출발 시각을 선택해주세요",
														},
													]}>
													<TimePicker
														inputReadOnly={isMobile}
														style={{
															width: "100%",
														}}
														format="HH:mm"
													/>
												</Form.Item>
												<Form.Item
													label="복귀 예정 시각"
													required
													style={{
														marginBottom: 0,
													}}>
													<p
														style={{
															marginTop: 0,
															marginBottom: 8,
															paddingLeft: 8,
															color: "#666",
														}}>
														복귀시간이
														정해져있지
														않은
														경우,
														대략적으로
														적어주시기
														바랍니다.
													</p>
													<Form.Item
														name="local_trip_return_type"
														rules={[
															{
																required: true,
																message: "복귀 예정 시각을 선택해주세요",
															},
														]}>
														<Select
															onChange={
																setReturnType
															}
															options={[
																{
																	value: "1시간 이내",
																	label: "1시간 이내",
																},
																{
																	value: "2시간 이내",
																	label: "2시간 이내",
																},
																{
																	value: "직접 선택",
																	label: "직접 선택",
																},
															]}
														/>
													</Form.Item>
												</Form.Item>
												{returnType ===
													"직접 선택" && (
													<Form.Item
														name="local_trip_return_time"
														label="복귀 시각"
														rules={[
															{
																required: true,
																message: "복귀 시각을 선택해주세요",
															},
														]}>
														<TimePicker
															inputReadOnly={isMobile}
															style={{
																width: "100%",
															}}
															format="HH:mm"
														/>
													</Form.Item>
												)}
												<Form.Item
													name="additional_users"
													label="추가 해당인">
													<Space
														direction="vertical"
														style={{
															width: "100%",
														}}>
														<Button
															icon={
																<UserOutlined />
															}
															onClick={() => {
																setAdditionalUsersModalVisible(
																	true,
																);
																fetchAvailableUsers();
															}}>
															추가
															해당인
															선택
														</Button>
														{selectedAdditionalUsers.length >
															0 && (
															<Space
																wrap>
																{selectedAdditionalUsers.map(
																	(
																		user,
																	) => (
																		<Tag
																			key={
																				user.id
																			}
																			closable
																			onClose={() => {
																				const next =
																					selectedAdditionalUsers.filter(
																						(
																							u,
																						) =>
																							u.id !==
																							user.id,
																					);
																				setSelectedAdditionalUsers(
																					next,
																				);
																				form.setFieldsValue(
																					{
																						additional_users:
																							next.map(
																								(
																									u,
																								) =>
																									u.id,
																							),
																					},
																				);
																			}}>
																			{
																				user.name
																			}{" "}
																			(
																			{getJobLabel(
																				user.job,
																				user.gender,
																			)}

																			)
																		</Tag>
																	),
																)}
															</Space>
														)}
													</Space>
												</Form.Item>
											</>
										)}
										<Form.Item>
											<Button
												type="primary"
												htmlType="submit"
												loading={
													loading
												}
												block>
												신청하기
											</Button>
										</Form.Item>
									</Form>
								</Card>
							</div>
						</div>
					</Content>
				</Layout>
			</Layout>
			<Modal
				title="동행 인원 선택"
				open={additionalUsersModalVisible}
				onOk={() => {
					form.setFieldsValue({
						additional_users:
							selectedAdditionalUsers.map(
								(u) => u.id,
							),
					});
					setAdditionalUsersModalVisible(false);
				}}
				onCancel={() =>
					setAdditionalUsersModalVisible(false)
				}
				width={640}>
				<Input.Search
					placeholder="이름 검색"
					value={searchText}
					onChange={(e) =>
						setSearchText(e.target.value)
					}
					style={{ marginBottom: 12 }}
				/>
				<Table
					rowKey="id"
					size="small"
					rowSelection={rowSelection}
					dataSource={filteredUsers}
					pagination={{ pageSize: 8 }}
					columns={[
						{
							title: "이름",
							dataIndex: "name",
						},
						{
							title: "직분",
							dataIndex: "classification_name",
						},
					]}
				/>
			</Modal>
		</ConfigProvider>
	);
};

export default ApplyForBusinessTrip;
